// #1082 店ジャンル（体験/飲食/お土産/その他）。オーナーが選ぶ上位区分で、
// ・ユーザーの一覧フィルタ
// ・オーナー設定での「機能デフォルト」流し込み＋無関係タブの折りたたみ
// を driving する。ユーザー画面の機能ボタン出し分けは既存フラグ（deliveryEnabled 等）
// が単一の真実で、genre はそのフラグの既定値を決めるだけ（＝上書き可・逃げ道あり）。
import type { MenuItem, Store } from '../store/useStore'

export type Genre = 'experience' | 'food' | 'souvenir' | 'other'

// 表示順（ユーザーフィルタ・オーナー picker 共通）
export const GENRES: Genre[] = ['food', 'souvenir', 'experience', 'other']

// i18n キーのサフィックス（顧客: stores.genre* / storeDetail.genre.* 、オーナー: storeSettings.genre*）
export const GENRE_KEY: Record<Genre, string> = {
  food: 'Food',
  souvenir: 'Souvenir',
  experience: 'Experience',
  other: 'Other',
}

/**
 * #1661 **category は「飲食の細分類（何の料理か）」だけ。** 店の業態は genre が持つ。
 *
 * 元は 1 つの category に「料理のジャンル (ramen/sushi/cafe…)」と「店の業態
 * (shop/hotel/other)」が同居していた。後から genre を上位区分として足したとき
 * category 側を整理しなかったため、
 *
 *   - 体験に対応する category が無く 'other' が代用された（= 本当のその他と区別不能）
 *   - お土産が 'shop' と 'souvenir' の 2 系統に割れた
 *   - 型に無い値 ('experience' / 'souvenir') が実データに入った
 *
 * という状態になっていた。業態を聞かれたら **genreOf() を使う**こと。
 */
export const FOOD_CATEGORIES: readonly NonNullable<Store['category']>[] = [
  'restaurant', 'cafe', 'izakaya', 'bar', 'ramen', 'sushi', 'bakery', 'sweets', 'kitchencar',
]

const FOOD_CATEGORY_SET = new Set<string>(FOOD_CATEGORIES)

/**
 * 業態を判定するための入力。
 *
 * category を **string で受ける**のは、正規化前のデータ（取引に残った
 * storeCategory 等）が型に無い値を持っているため。ここは旧データの
 * 受け皿なので、型を狭めると呼び出し側が cast だらけになる。
 */
export type StoreTypeInput = { genre?: Genre; category?: string }

/**
 * 店の業態。**backend が genre を返すのが正**で、ここは古いデータ用の fallback。
 *
 * 正規化前のデータには業態が category 側に入っているものがあるので、それも拾う
 * ('shop'/'souvenir' → お土産、'experience' → 体験)。移行後は genre が必ず入る。
 */
export function genreOf(store: StoreTypeInput): Genre {
  if (store.genre) return store.genre
  const legacy = store.category as string | undefined
  if (legacy && FOOD_CATEGORY_SET.has(legacy)) return 'food'
  if (legacy === 'shop' || legacy === 'souvenir') return 'souvenir'
  if (legacy === 'experience') return 'experience'
  return 'other'
}

/**
 * 画面に出す「店の種類」の i18n キー。
 *
 * 飲食は category（「ラーメン」の方が「飲食」より役に立つ）、それ以外は genre。
 * **category を直接引かないこと** — 非飲食の店は category を持たない。
 */
export function storeTypeLabelKey(
  store: StoreTypeInput, prefix = 'storeDetail.category',
): string {
  const genre = genreOf(store)
  if (genre === 'food' && store.category && FOOD_CATEGORY_SET.has(store.category)) {
    return prefix + '.' + store.category
  }
  return prefix + '.' + genre
}

// ジャンル選択/変更時にフラグへ流し込む既定（epic #1082 の表）。
// ・✗ の機能は false / 'pay-only' に落とす（客側で自動的に非表示になる）
// ・✓ の機能（体験の予約）は true にする
// ・food は「何でも可」なので何も強制しない（既存設定を維持）
// オーナーは詳細設定でいつでも上書きできる。
export function genreDefaultFlags(genre: Genre): Partial<Store> {
  switch (genre) {
    case 'food':
      return {}
    case 'souvenir':
      return { reservationEnabled: false, deliveryEnabled: false, orderingMode: 'pay-only' }
    case 'experience':
      return { reservationEnabled: true, deliveryEnabled: false, orderingMode: 'pay-only' }
    case 'other':
      return { deliveryEnabled: false, orderingMode: 'pay-only' }
  }
}

// #1182/#1181 物販 (お土産・グッズ)。商品は menu サブコレクション上で isRetail=true の
// item として持ち、店舗管理の「商品」タブから登録する (タブがフラグを決める —
// ユーザーにチェックボックスは見せない)。レジの物販画面・QRラベル・EC連携は
// この関数が返す商品だけを対象にする。
export function retailItemsOf(menu: MenuItem[] | undefined): MenuItem[] {
  return (menu || []).filter((m) => m.isRetail === true)
}

// レジに物販画面を出すか: お土産ジャンル (商品が主業) か、商品を登録済みの店
// (飲食店が「詳細設定を表示」で商品タブを開いて併売する例外ケース)。
export function hasRetailSales(store: Pick<Store, 'genre' | 'category' | 'menu'>): boolean {
  return genreOf(store) === 'souvenir' || retailItemsOf(store.menu).length > 0
}

/**
 * アイコン・配色を引くためのキー。飲食は料理の種類、それ以外は業態。
 *
 * 見た目のマップ (categoryIcons / categoryGradients) は文字列キーなので、
 * **非飲食の店で undefined を渡さない**ためにここを通す。
 */
export function storeVisualKey(store: StoreTypeInput): string {
  // **ラベルと同じ選び方にする。** category を無条件に優先すると、正規化前の
  // 「体験なのに category='other'」の店がラベル「体験」・アイコン灰色の🏪 になる
  if (store.category && FOOD_CATEGORY_SET.has(store.category)) return store.category
  return genreOf(store)
}

/**
 * ジャンルを選び直したときの category。
 *
 * #1661 **非飲食は undefined を返す。** 以前は souvenir→'shop' / experience→'other'
 * のようなダミーを書き込んでおり、これが「体験なのに category が other」という
 * 実データ（5 店）を作った張本人だった。業態は genre が持つので代用は要らない。
 */
export function genreDefaultCategory(genre: Genre): Store['category'] | undefined {
  return genre === 'food' ? 'restaurant' : undefined
}

// オーナー設定で、そのジャンルに関連するタブ（既定で表示するもの）。
// ここに無いタブは「詳細設定を表示」で開放する（＝完全に隠さない・逃げ道）。
// info/sns/staff は常時表示なので対象外。#1181 案B: menu(飲食)/products(物販) も
// ジャンルで出し分ける — お土産店は「商品」だけ、飲食店は「メニュー」だけが既定。
export type GenreTab = 'menu' | 'products' | 'reservations' | 'ordering' | 'delivery'

export function genreRelevantTabs(genre: Genre): GenreTab[] {
  switch (genre) {
    case 'food':
      return ['menu', 'reservations', 'ordering', 'delivery']
    case 'experience':
      return ['reservations']
    case 'souvenir':
      return ['products']
    case 'other':
      return ['menu', 'reservations']
  }
}

/**
 * #1484 「レシート」がその店に自然か。
 *
 * **物販も自然。** 直売所で梨を買えばレシートが出る。不自然なのは体験の方
 * (ガイド周遊に「レシート」は出さない)。
 */
export function usesReceiptWord(genre: Genre | undefined | null): boolean {
  return genre === 'food' || genre === 'souvenir'
}

/**
 * #1496 「厨房」がその店に自然か。
 *
 * **飲食だけ。物販に厨房は無い。** 「レシート」と分かれ方が違うので、
 * 同じ述語では賄えない (物販は レシート ○ / 厨房 ×)。
 */
export function usesKitchenWord(genre: Genre | undefined | null): boolean {
  return genre === 'food'
}
